import { useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { useQueryClient } from '@tanstack/react-query';

import type { TEmailValidationBranch } from '../../AnonymousSplitTest/useEmailValidationTest';
import {
  getEmailValidationQueryOptions,
  useGetEmailValidationQuery,
} from '../../api/lendingApi/lendingApiQueries';

export const INVALID_EMAIL_MESSAGE =
  'We can’t verify this email address. Check for typos and try again, or give us a call.';

export const useEmailValidation = ({
  enabled,
  isBlocking,
  branch,
}: {
  enabled: boolean;
  isBlocking: boolean;
  branch: TEmailValidationBranch;
}) => {
  const queryClient = useQueryClient();
  const [checkedEmail, setCheckedEmail] = useState<string>();
  const trackedEmails = useRef(new Set<string>());
  const { emailValidation } = useGetEmailValidationQuery(
    enabled ? checkedEmail : undefined,
  );

  useEffect(() => {
    if (!emailValidation || !checkedEmail || !branch) return;
    if (trackedEmails.current.has(checkedEmail)) return;

    trackedEmails.current.add(checkedEmail);
    tracking.ampli.emailValidationChecked({
      valid: emailValidation.valid,
      has_mx_or_a_record: emailValidation.has_mx_or_a_record ?? undefined,
      has_known_bounces: emailValidation.has_known_bounces ?? undefined,
      email_validation_split_test: branch,
    });
  }, [emailValidation, checkedEmail, branch]);

  const checkEmailValidityOnBlur = async ({
    email,
    validateEmail,
  }: {
    email: string;
    validateEmail: () => Promise<boolean>;
  }) => {
    if (!enabled || !email) return;

    if (!(await validateEmail())) return;

    setCheckedEmail(email);
  };

  const checkEmailValidityOnSubmit = async (email: string) => {
    if (!enabled || !isBlocking) return true;

    setCheckedEmail(email);

    try {
      const { valid } = await queryClient.ensureQueryData(
        getEmailValidationQueryOptions(email),
      );

      return valid;
    } catch {
      return true;
    }
  };

  const isShowingInvalidEmail = (email: string) =>
    isBlocking && emailValidation?.valid === false && checkedEmail === email;

  return {
    checkEmailValidityOnBlur,
    checkEmailValidityOnSubmit,
    isShowingInvalidEmail,
  };
};
